<template>
  <button
    :class="[
      'button',
      {
        'size-extra-small': props.sizeExtraSamll,
        'size-small': props.sizeSmall,
        'size-medium': props.sizeMedium,
        'size-large': props.sizeLarge,
        'color-primary': props.colorPrimary,
        'color-secondary': props.colorSecondary,
        'color-tertiary': props.colorTertiary,
        'color-red': props.colorRed,
        'color-green': props.colorGreen,
        'color-orange': props.colorOrange,
        'theme-outline': props.themeOutline,
        'state-disabled': props.stateDisabled,
      },
      props.className,
    ]"
    :style="[props.inlineStyles]"
    @click="onClick"
  >
    <span class="button-text">
      <slot />
    </span>
  </button>
</template>

<script setup>
// Component name
const componentName = 'Button';

// Component props types: String, Number, Boolean, Array, Object, Function, Promise
const props = defineProps({
  className: {
    type: String,
  },
  inlineStyles: {
    type: Object,
    default: function () {
      return {
        // Style
      };
    },
  },
  sizeExtraSamll: {
    type: Boolean,
  },
  sizeSmall: {
    type: Boolean,
  },
  sizeMedium: {
    type: Boolean,
  },
  sizeLarge: {
    type: Boolean,
  },
  colorPrimary: {
    type: Boolean,
  },
  colorSecondary: {
    type: Boolean,
  },
  colorTertiary: {
    type: Boolean,
  },
  colorRed: {
    type: Boolean,
  },
  colorGreen: {
    type: Boolean,
  },
  colorOrange: {
    type: Boolean,
  },
  themeOutline: {
    type: Boolean,
  },
  stateDisabled: {
    type: Boolean,
  },
  onClick: {
    type: Function,
    default: function () {},
  },
});

const onClick = () => {
  if (props.stateDisabled) {
  } else {
    props.onClick();
  }
};
</script>

<style lang="scss">
.button {
  /* Parent style   ------------------------------ */
  transition: $transition;

  /* Children style ------------------------------ */
  .button-text {
    display: inline-block;
    padding: 0 $px-3d5;
  }

  /* Modifier style ------------------------------ */
  /* Sizes */
    &.size-extra-small {
    /* Layout */
     min-width: 4rem; // [Design note] min-width CSS property can't display in Figma view mode
    height: $semantic-dimension-cta-height-sm;
    padding: $py-1 $px-2;
    border-radius: $semantic-dimension-cta-rounded-md;
    /* Text : semantic/body/sm-leading-none; */
    font-family: $typography-font-family-primary;
    font-size: $semantic-text-body-xs-font-size;
    font-weight: 400; // [Design note] Don't use variable because style dictionary convert number to rem unit (* Invalid CSS property value)
    line-height: $semantic-text-body-xs-line-height-leading-none;
  }

  &.size-small {
    /* Layout */
    min-width: $specific-button-min-width-sm; // [Design note] min-width CSS property can't display in Figma view mode
    height: $semantic-dimension-cta-height-sm;
    padding: $py-2 $px-4;
    border-radius: $semantic-dimension-cta-rounded-md;
    /* Text : semantic/body/sm-leading-none; */
    font-family: $typography-font-family-primary;
    font-size: $semantic-text-body-sm-font-size;
    font-weight: 400; // [Design note] Don't use variable because style dictionary convert number to rem unit (* Invalid CSS property value)
    line-height: $semantic-text-body-sm-line-height-leading-none;
  }

  &.size-medium {
    /* Layout */
    min-width: $specific-button-min-width-md; // [Design note] min-width CSS property can't display in Figma view mode
    height: $semantic-dimension-cta-height-md;
    padding: $py-2 $px-4;
    border-radius: $semantic-dimension-cta-rounded-md;
    /* Text : semantic/body/md-leading-none; */
    font-family: $typography-font-family-primary;
    font-size: $semantic-text-body-md-font-size;
    font-weight: 400; // [Design note] Don't use variable because style dictionary convert number to rem unit (* Invalid CSS property value)
    line-height: $semantic-text-body-md-line-height-leading-none;
  }

  &.size-large {
    /* Layout */
    min-width: $specific-button-min-width-lg; // [Design note] min-width CSS property can't display in Figma view mode
    height: $semantic-dimension-cta-height-lg;
    padding: $py-2 $px-4;
    border-radius: $semantic-dimension-cta-rounded-lg;
    /* Text : semantic/body/lg-leading-none; */
    font-family: $typography-font-family-primary;
    font-size: $semantic-text-body-lg-font-size;
    font-weight: 400; // [Design note] Don't use variable because style dictionary convert number to rem unit (* Invalid CSS property value)
    line-height: $semantic-text-body-lg-line-height-leading-none;
  }

  /* Colors */
  &.color-primary {
    /* Layout */
    background: $specific-button-primary-background;
    /* Text */
    color: $specific-button-primary-foreground;

    &:hover {
      background: $specific-button-primary-background-hover;
    }

    &:active {
      background: $specific-button-primary-background-active;
    }

    &:focus {
      box-shadow: 0 0 0 2px $specific-button-primary-ring-offset,
        // ring-offset-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
        0 0 0 4px $specific-button-primary-ring; // ring-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
    }
  }

  &.color-secondary {
    /* Layout */
    background: $specific-button-secondary-background;
    /* Text */
    color: $specific-button-secondary-foreground;

    &:hover {
      background: $specific-button-secondary-background-hover;
    }

    &:active {
      background: $specific-button-secondary-background-active;
    }

    &:focus {
      box-shadow: 0 0 0 2px $specific-button-secondary-ring-offset,
        // ring-offset-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
        0 0 0 4px $specific-button-secondary-ring; // ring-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
    }
  }

  &.color-tertiary {
    /* Layout */
    background: $specific-button-tertiary-background;
    /* Text */
    color: $specific-button-tertiary-foreground;

    &:hover {
      background: $specific-button-tertiary-background-hover;
    }

    &:active {
      background: $specific-button-tertiary-background-active;
    }

    &:focus {
      box-shadow: 0 0 0 2px $specific-button-tertiary-ring-offset,
        // ring-offset-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
        0 0 0 4px $specific-button-tertiary-ring; // ring-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
    }
  }

  &.color-red {
    /* Layout */
    background: $specific-button-red-background;
    /* Text */
    color: $specific-button-red-foreground;

    &:hover {
      background: $specific-button-red-background-hover;
    }

    &:active {
      background: $specific-button-red-background-active;
    }

    &:focus {
      box-shadow: 0 0 0 2px $specific-button-red-ring-offset,
        // ring-offset-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
        0 0 0 4px $specific-button-red-ring; // ring-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
    }
  }

  &.color-green {
    /* Layout */
    background: $specific-button-green-background;
    /* Text */
    color: $specific-button-green-foreground;

    &:hover {
      background: $specific-button-green-background-hover;
    }

    &:active {
      background: $specific-button-green-background-active;
    }

    &:focus {
      box-shadow: 0 0 0 2px $specific-button-green-ring-offset,
        // ring-offset-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
        0 0 0 4px $specific-button-green-ring; // ring-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
    }
  }

  &.color-orange {
    /* Layout */
    background: $specific-button-orange-background;
    /* Text */
    color: $specific-button-orange-foreground;

    &:hover {
      background: $specific-button-orange-background-hover;
    }

    &:active {
      background: $specific-button-orange-background-active;
    }

    &:focus {
      box-shadow: 0 0 0 2px $specific-button-orange-ring-offset,
        // ring-offset-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
        0 0 0 4px $specific-button-orange-ring; // ring-width, [Design note] Shadow variable can't display in Figma view mode, Please contact designer for variable name
    }
  }

  /* Themes */
  &.theme-outline {
    /* Colors */
    &.color-primary {
      /* Layout */
      background: transparent;
      border: 1px solid $specific-button-primary-border;
      /* Text */
      color: $specific-button-primary-foreground-outline;

      &:hover {
        /* Layout */
        border: 1px solid $specific-button-primary-border-hover;
        /* Text */
        color: $specific-button-primary-foreground-outline-hover;
      }

      &:active {
        /* Layout */
        border: 1px solid $specific-button-primary-border-active;
        /* Text */
        color: $specific-button-primary-foreground-outline-active;
      }
    }

    &.color-secondary {
      /* Layout */
      background: transparent;
      border: 1px solid $specific-button-secondary-border;
      /* Text */
      color: $specific-button-secondary-foreground-outline;

      &:hover {
        /* Layout */
        border: 1px solid $specific-button-secondary-border-hover;
        /* Text */
        color: $specific-button-secondary-foreground-outline-hover;
      }

      &:active {
        /* Layout */
        border: 1px solid $specific-button-secondary-border-active;
        /* Text */
        color: $specific-button-secondary-foreground-outline-active;
      }
    }

    &.color-tertiary {
      /* Layout */
      background: transparent;
      border: 1px solid $specific-button-tertiary-border;
      /* Text */
      color: $specific-button-tertiary-foreground-outline;

      &:hover {
        /* Layout */
        border: 1px solid $specific-button-tertiary-border-hover;
        /* Text */
        color: $specific-button-tertiary-foreground-outline-hover;
      }

      &:active {
        /* Layout */
        border: 1px solid $specific-button-tertiary-border-active;
        /* Text */
        color: $specific-button-tertiary-foreground-outline-active;
      }
    }

    &.color-red {
      /* Layout */
      background: transparent;
      border: 1px solid $specific-button-red-border;
      /* Text */
      color: $specific-button-red-foreground-outline;

      &:hover {
        /* Layout */
        border: 1px solid $specific-button-red-border-hover;
        /* Text */
        color: $specific-button-red-foreground-outline-hover;
      }

      &:active {
        /* Layout */
        border: 1px solid $specific-button-red-border-active;
        /* Text */
        color: $specific-button-red-foreground-outline-active;
      }
    }

    &.color-green {
      /* Layout */
      background: transparent;
      border: 1px solid $specific-button-green-border;
      /* Text */
      color: $specific-button-green-foreground-outline;

      &:hover {
        /* Layout */
        border: 1px solid $specific-button-green-border-hover;
        /* Text */
        color: $specific-button-green-foreground-outline-hover;
      }

      &:active {
        /* Layout */
        border: 1px solid $specific-button-green-border-active;
        /* Text */
        color: $specific-button-green-foreground-outline-active;
      }
    }

    &.color-orange {
      /* Layout */
      background: transparent;
      border: 1px solid $specific-button-orange-border;
      /* Text */
      color: $specific-button-orange-foreground-outline;

      &:hover {
        /* Layout */
        border: 1px solid $specific-button-orange-border-hover;
        /* Text */
        color: $specific-button-orange-foreground-outline-hover;
      }

      &:active {
        /* Layout */
        border: 1px solid $specific-button-orange-border-active;
        /* Text */
        color: $specific-button-orange-foreground-outline-active;
      }
    }
  }

  /* State : Disabled */
  &.state-disabled {
    pointer-events: none;

    &.color-primary,
    &.color-secondary,
    &.color-tertiary,
    &.color-red,
    &.color-green,
    &.color-orange {
      /* Layout */
      background: $semantic-color-status-disabled-background;
      /* Text */
      color: $semantic-color-status-disabled-foreground;
    }

    &.theme-outline {
      &.color-primary,
      &.color-secondary,
      &.color-tertiary,
      &.color-red,
      &.color-green,
      &.color-orange {
        /* Layout */
        background: transparent;
        border: 1px solid $semantic-color-status-disabled-border;
        /* Text */
        color: $semantic-color-status-disabled-foreground;
      }
    }
  }

  /* Other component style ----------------------- */
}
</style>
